import {Link, Navigate, Route, Routes} from 'react-router-dom';
import {BrandsRoutes} from '@tinct/web-feature-brand';
import {UserLayout} from '@tinct/web-layout';
import {PenSquareIcon} from 'lucide-react';
import {
  CampaignsRoutes,
  CampaignStudioPage,
  FunnelCampaignsRoutes,
  NEW_CAMPAIGN_DIALOG_HASH,
  NewCampaignDialog,
  VariantPreviewPage,
} from '@tinct/web-feature-campaigns';
import {useConnectedUser} from '@tinct/core-store-session';
import {useGetCampaigns} from '@tinct/core-api';
import {User} from '@tinct/core-api-models';
import {supportNavigation} from '../navigation/support-navigation';
import {useMainNavigation} from '../hooks/use-main-navigation';
import {useWorkspaceSwitcher} from '../hooks/use-workspace-switcher';
import {AudiencesRoutes, ResumeAudienceImportModal} from '@tinct/web-feature-companies';
import {DashboardRoutes} from '@tinct/web-feature-dashboard';
import {CheckoutSucceedModal, PastDueInvoiceBanner, PaywallProRequiredModal, TinctPlanCard} from "@tinct/web-feature-payment";
import {AccountRoutes} from "@tinct/web-feature-users";
import {WorkspaceSettingsRoutes} from './workspace-settings-routes';
import {useUserNavigation} from "../hooks/use-user-navigation";

import {
  AgencyRoutes,
  OrganizationDispatcherPage,
  useCreateChildOrganizationModal,
  useCreateUserOrganizationModal,
  WorkspacesRoutes,
  AgencySeatsCard,
} from "@tinct/web-feature-organizations";
import {useCurrentOrganization, useGetCurrentWorkspaceId, useIsAgencyWorkspace} from "@tinct/core-workspace";
import {useAgencyNavigation} from '../hooks/use-agency-navigation';
import {Button, Tooltip, TooltipContent, TooltipTrigger} from "@tinct/web-components";

export const WorkspaceRoutes = () => {
  const connectedUser = useConnectedUser() as User;
  const organizationId = useGetCurrentWorkspaceId();

  const baseWorkspaceUrl = `/w/${organizationId}`;

  // Agencies are content shells (every content endpoint 403s for them): they
  // get their own navigation, no campaign widgets, and "add workspace" creates
  // a managed child workspace instead of a standalone one.
  const isAgency = useIsAgencyWorkspace();
  const mainNavigation = useMainNavigation(baseWorkspaceUrl);
  const agencyNavigation = useAgencyNavigation(baseWorkspaceUrl);
  const userNavigation = useUserNavigation(baseWorkspaceUrl);

  const { Modal: CreateWorkspaceModal, open: openCreateWorkspace } = useCreateUserOrganizationModal({
    userId: connectedUser.id as string,
  });
  const { Modal: CreateChildWorkspaceModal, open: openCreateChildWorkspace } = useCreateChildOrganizationModal({
    agencyId: organizationId,
  });

  const workspaceSwitcher = useWorkspaceSwitcher({
    userId: connectedUser.id as string,
    selectedWorkspaceId: organizationId,
    settingsUrl: `${baseWorkspaceUrl}/settings`,
    onAddWorkspace: () => (isAgency ? openCreateChildWorkspace() : openCreateWorkspace()),
  });

  const { data: campaigns } = useGetCampaigns({
    limit: 10,
    offset: 0,
    staticParams: {
      organizationId: organizationId,
    }
  }, {
    enabled: !isAgency,
  })

  const recentCampaignsSection = {
    sectionName: 'Recent campaigns',
    items: (campaigns?.data || []).map((campaign) => ({
      title: campaign.name ?? 'Untitled',
      url: `${baseWorkspaceUrl}/campaigns/${campaign.id}`,
    })),
  };

  const organization = useCurrentOrganization();

  return (
    <>
      <Routes>
        <Route path="dispatch" element={<OrganizationDispatcherPage />} />
        {!isAgency && (
          <>
            <Route path="brand/*" element={<BrandsRoutes />} />
            <Route path="funnel-campaigns/*" element={<FunnelCampaignsRoutes />} />
            <Route path="campaigns/:id/landing-pages/:variantId/preview" element={<VariantPreviewPage />} />
            <Route path="campaigns/:id/studio" element={<CampaignStudioPage />} />
          </>
        )}
        <Route path="settings/*" element={<WorkspaceSettingsRoutes baseWorkspaceUrl={baseWorkspaceUrl} />} />
        <Route
          element={
          <div>
            <UserLayout
              banner={<PastDueInvoiceBanner />}
              cardPlan={isAgency ? <AgencySeatsCard /> : <TinctPlanCard />}
              navUserItems={userNavigation}
              connectedUser={connectedUser}
              headerAction={isAgency ? undefined : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button variant="outline" size="icon" disabled={organization?.lastBrandVersion == null} asChild={organization?.lastBrandVersion != null}>
                        <Link to={{hash: NEW_CAMPAIGN_DIALOG_HASH}}>
                          <PenSquareIcon className="size-4" />
                        </Link>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {organization?.lastBrandVersion == null && (
                    <TooltipContent>
                      Complete your brand analysis first
                    </TooltipContent>
                  )}
                </Tooltip>
              )}
              workspaces={workspaceSwitcher}
              mainNavigationGroups={[
                {
                  items: isAgency ? agencyNavigation : mainNavigation,
                },
                ...(!isAgency && recentCampaignsSection.items.length > 0 ? [recentCampaignsSection] : []),
              ]}
              footerNavigationGroups={[
                {
                  sectionName: 'Support',
                  items: supportNavigation,
                },
              ]}
            />
            <CreateWorkspaceModal />
            <CreateChildWorkspaceModal />
          </div>
          }
        >
          <Route path="account/*" element={<AccountRoutes baseUrl={baseWorkspaceUrl} />} />
          {isAgency ? (
            <Route path="agency/*" element={<AgencyRoutes />} />
          ) : (
            <>
              <Route path="dashboard/*" element={<DashboardRoutes />} />
              <Route path="campaigns/*" element={<CampaignsRoutes />} />
              <Route path="audiences/*" element={<AudiencesRoutes />} />
            </>
          )}
          <Route path="workspaces/*" element={<WorkspacesRoutes />} />
          <Route path='*' element={<Navigate to={`${baseWorkspaceUrl}/dispatch`} replace />} />
        </Route>
      </Routes>
      <PaywallProRequiredModal />
      <CheckoutSucceedModal />
      <ResumeAudienceImportModal />
      <NewCampaignDialog />
    </>

  );
};
